import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { faAngleDown, faExclamationCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { Subject } from 'rxjs';
import { last } from 'cypress/types/lodash';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

export interface iacOptionInterface {
	value: any;
	name: any;
	secondName?: any;
	commonGroup?: any;
	tip?: string;
	disabled?: boolean;
}

@Component({
	standalone: true,
	imports: [CommonModule, FontAwesomeModule],
	selector: 'cpi-input-with-autocomplete',
	templateUrl: './input-with-autocomplete.component.html',
	styleUrls: ['./input-with-autocomplete.component.scss'],
})
export class InputWithAutocompleteComponent implements OnInit, OnChanges {
	faAngleDown = faAngleDown;
	faTimes = faTimes;
	faExclamationCircle = faExclamationCircle;
	inputWithAutoCompleteFocus = false;

	@ViewChild('inputFilteredList')
	divInputFilteredList: ElementRef;

	@ViewChild('inputWithAutoComplete')
	inputWithAutoComplete!: ElementRef<HTMLInputElement>;

	@Input()
	cleanValues = false;
	@Input()
	alert = false;
	@Input()
	unselectValue = false;
	@Input()
	availableOptions: iacOptionInterface[] = [];
	@Input()
	availableOptionsGenericForm: genericFormI<'item'>[] = [];
	filteredOptions: iacOptionInterface[] = [];
	lastFilterHover = 0;

	filterFunction: Function = this.defaultFilterFunction;

	@Input() setValue: any = '';
	@Output() onValueChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onCleanValues = new EventEmitter();
	@Output() onValueSelected = new EventEmitter();

	@Output()
	getInputText: EventEmitter<any> = new EventEmitter<any>();
	@Input()
	setInputText: any = '';

	@Output()
	whenCleanInputText = new EventEmitter();

	selectedOption: iacOptionInterface;

	@Output()
	getIndex: EventEmitter<any> = new EventEmitter<any>();

	private internalChange: Subject<void> = new Subject<void>();
	//Config
	@Input()
	minInputLength = 3;
	@Input()
	placeholder = '';
	@Input()
	inputHeight = '';
	@Input()
	keepValue = true;
	openTop = false;
	openOptions = true;
	@Input()
	idGen = '';
	@Input()
	disabled = false;
	//red alert on field if you type an invalid value
	@Input()
	allowFreeText = true;

	constructor() {
		if (this.idGen === '') this.idGen = 'iac' + Math.floor(Math.random() * 99_999);
	}

	ngOnInit(): void {
		if (this.minInputLength < 0) this.minInputLength = 0;
		if (!this.availableOptions && !this.availableOptionsGenericForm) throw 'Available options not defined';
		else {
			this.initIAC();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.cleanValues && changes.cleanValues.currentValue === true) {
			this.setValue = '';
			this.setInputText = '';
			this.filteredOptions = [];
			if (this.minInputLength <= 0) this.filterFunction();
			setTimeout(() => {
				this.cleanValues = false;
			}, 1);
		}

		if (changes && changes.unselectValue && changes.unselectValue.currentValue === true) {
			this.setValue = '';
			this.setInputText = '';
			this.filteredOptions = this.availableOptions;
			setTimeout(() => {
				this.unselectValue = false;
			}, 1);
		}

		if (
			changes &&
			changes.setInputText &&
			changes.setInputText.currentValue != changes.setInputText.previousValue &&
			this.internalChange.observers.length > 0
		) {
			this.filterFunction();
		}

		if (
			changes &&
			((changes.availableOptions && changes.availableOptions.currentValue != changes.availableOptions.previousValue) ||
				(changes.availableOptionsGenericFormItem &&
					changes.availableOptionsGenericFormItem.currentValue != changes.availableOptionsGenericFormItem.previousValue))
		) {
			this.cleanValues = true;
			this.initIAC();
		}

		if (changes && changes.setValue && this.availableOptions.length > 0) {
			this.setInputTextFromValue();
			this.emitInputText(this.setInputText);
		}
	}

	private initIAC(): void {
		if ((!this.availableOptions || (this.availableOptions && this.availableOptions.length === 0)) && this.availableOptionsGenericForm) {
			const availableOptions: iacOptionInterface[] = [];
			for (const item of this.availableOptionsGenericForm) {
				const option: iacOptionInterface = {
					name: item.label ? item.label : item.name,
					value: item.values[0],
					disabled: item.disabled?.toString() == 'true',
				};
				availableOptions.push(option);
			}
			this.availableOptions = availableOptions;
			if (this.minInputLength === 0) this.filteredOptions = this.availableOptions;
			if (this.setValue) {
				this.setInputTextFromValue();
			}
			this.emitInputText(this.setInputText);
			this.toOpenTop();
		} else if (this.availableOptions && this.availableOptions.length > 0) {
			let holdAvailableOptions: iacOptionInterface[] = [];
			holdAvailableOptions = [...this.availableOptions];

			this.availableOptions = [];
			setTimeout(() => {
				this.availableOptions = holdAvailableOptions;
				if (this.minInputLength === 0) this.filteredOptions = this.availableOptions;
				if (this.setValue) {
					this.setInputTextFromValue();
				}
				this.emitInputText(this.setInputText);
				this.toOpenTop();
			}, 10);
		}
	}

	private defaultFilterFunction(): void {
		if (this.setInputText?.length >= this.minInputLength) {
			this.filteredOptions = [];
			for (const option of this.availableOptions) {
				if (
					option.name?.toString().toLocaleLowerCase().includes(this.setInputText.toLocaleLowerCase()) ||
					option.secondName?.toString().toLocaleLowerCase().includes(this.setInputText.toLocaleLowerCase())
				) {
					this.filteredOptions.push(option);
				}
			}
		} else {
			this.filteredOptions = this.minInputLength === 0 ? this.availableOptions : [];
		}
		setTimeout(() => {
			this.toOpenTop();
		}, 0);
	}

	protected keyboardHandler(event: KeyboardEvent): void {
		const keys = ['ArrowDown', 'ArrowUp', 'Enter'];

		if (
			this.openOptions == false ||
			!keys.includes(event.key) ||
			!this.filteredOptions ||
			(this.filteredOptions && this.filteredOptions.length === 0)
		)
			return;

		if (event.key === 'ArrowDown' && this.lastFilterHover != this.filteredOptions.length - 1) {
			this.lastFilterHover++;
		}
		if (event.key === 'ArrowUp' && this.lastFilterHover != 0) {
			this.lastFilterHover--;
		}

		if (event.key === 'Enter') {
			const item: iacOptionInterface = this.filteredOptions[this.lastFilterHover];
			if (item.disabled == true) return;
			this.lastFilterHover = 0;
			this.filteredOptions = this.availableOptions;

			if (this.keepValue) {
				this.selectedOption = item;
				this.setValue = item.value;
				this.setInputText = item.name;
			}

			this.emitIndex(this.availableOptions.indexOf(item));
			this.emitValue(item.value);
			this.emitInputText(item.name);

			this.openOptions = false;
		}
	}

	protected removeSelectedOption(): void {
		this.setValue = '';
		this.setInputText = '';
		this.emitIndex(-1);
		this.emitValue();
		this.emitInputText();
		this.filterFunction();
		this.focusMainInput();
	}

	private setInputTextFromValue(): boolean {
		let foundItem = false;
		if (this.availableOptions.length === 0 || this.setValue == '' || this.setValue == undefined || this.setValue == undefined) return;

		for (let index = 0; index < this.availableOptions.length; index++) {
			const option = this.availableOptions[index];
			if (option.value != this.setValue) continue;

			this.selectedOption = option;
			this.setInputText = option.name;
			foundItem = true;
		}

		if (!foundItem) {
			console.warn(
				`Input with autocomplete (${this.idGen}) erased setValue because it could not find a item with this value (${this.setValue})`
			);
			this.setValue = '';
		}
		if (this.setValue == '' || this.setValue == 0 || this.setValue == undefined || this.setValue == undefined) this.setInputText = '';
		this.filteredOptions = this.availableOptions;
	}

	protected handleClickOption(option: iacOptionInterface, index: number): void {
		if (option.disabled == true) return;
		this.openOptions = false;
		this.lastFilterHover = 0;
		this.alert = false;
		this.filteredOptions = this.availableOptions;

		if (this.keepValue) {
			this.selectedOption = option;
			this.setValue = option.value;
			this.setInputText = option.name;
		}

		this.emitValue(option.value);
		this.emitInputText(option.name);
		this.emitIndex(this.availableOptions.indexOf(this.filteredOptions[index]));
	}

	protected toOpenTop(): void {
		if (this.divInputFilteredList) {
			const divInputFilteredListBound = $.extend(
				true,
				{},
				(<HTMLElement>this.divInputFilteredList.nativeElement).getBoundingClientRect()
			);

			if (divInputFilteredListBound.y + divInputFilteredListBound.height > window.innerHeight) {
				this.openTop = true;
			}
		}
	}

	protected emitValue(value?: any): void {
		const emitValue = value == undefined ? this.setValue : value;
		this.onValueChange.emit(emitValue);
		if (emitValue == undefined || emitValue == '') this.emitOnCleanValues();
		else this.emitOnValueSelected();
	}
	protected emitOnCleanValues() {
		this.onCleanValues.emit(true);
	}
	protected emitOnValueSelected() {
		this.onValueSelected.emit(true);
	}

	protected emitInputText(inputText?: any): void {
		const emitValue = inputText == undefined ? this.setInputText : inputText;
		this.getInputText.emit(emitValue);
		if (emitValue == undefined || emitValue == '') this.emitOnClearInputText();
	}
	protected emitOnClearInputText(): void {
		this.whenCleanInputText.emit(true);
	}

	protected emitIndex(index: number): void {
		this.getIndex.emit(index);
	}

	public focusMainInput(): void {
		const inputElement = this.inputWithAutoComplete.nativeElement;
		inputElement.focus();
		inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
	}
}
